import dayjs from "dayjs";
import {
  getOrderDirection,
  shouldDisableOrderBusinessPlanSelect,
  shouldDisableOrderRouteBaseSelect,
} from "./helpers";
import {
  businessPlanLookupTable,
  customerLookupTable,
  type LookupFilters,
  routeBaseLookupTable,
  truckVendorLookupTable,
} from "../../shared/lookupTables";
import { formatOrderStagePeriod } from "../../shared/helpers/orderStagePeriod";

type BuildOrderColumnsParams = {
  activeTab: string;
  options: any;
  permissionRegions: { label: string; value: string }[];
  permissionDirections: { label: string; value: string }[];
  apiDebounceCustomer: (search: string) => Promise<any>;
  apiDebouncePAKD: (
    search: string,
    customerId: string,
    filters?: LookupFilters,
  ) => Promise<any>;
  apiDebounceRoute: (
    search: string,
    value?: string,
    filters?: LookupFilters,
  ) => Promise<any>;
  // apiDebounceRegion: (search: string) => Promise<any>;
  apiDebounceTruckVendor: (
    search: string,
    truckVendorType: "OWNER" | "CONTRACTOR" | "",
    ownershipFilter?: string,
    filters?: LookupFilters,
  ) => Promise<any>;
};

export const buildOrderColumns = ({
  activeTab,
  options,
  permissionRegions,
  permissionDirections,
  apiDebounceCustomer,
  apiDebouncePAKD,
  apiDebounceRoute,
  apiDebounceTruckVendor,
}: BuildOrderColumnsParams): any[] => {
  const creatorColumn = {
    title: "Người tạo",
    dataIndex: "createdBy",
    filterDataIndex: "createdBy",
    fixed: false,
    hidden: false,
    filterConfig: {
      type: "string",
      filterProps: {
        placeholder: "Nhập người tạo",
      },
    },
    render: (createdBy: any, record: any) => {
      const accountName = createdBy?.accountName;
      const createdAt = record?.createdAt;

      if (!accountName) return "";
      return createdAt
        ? `${accountName} - ${dayjs(createdAt).format("DD/MM/YYYY HH:mm:ss")}`
        : accountName;
    },
  };

  const columnsBaseCreateOrderTableConfig = [
    {
      title: "#",
      dataIndex: "_id",
      width: 50,
      fixed: false,
      hidden: false,
      render: (row: any, record: any, index: number) => index + 1,
    },
    {
      title: "Ngày đóng hàng",
      dataIndex: "loadingDate",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "dateRange",
        filterProps: {
          placeholder: "Chọn ngày đóng hàng",
        },
      },
      editType: "DatePicker",
      editProps: {
        allowClear: false,
      },
      colReturn: (row: any) => dayjs(row).format("DD/MM/YYYY"),
    },
    {
      title: "Ngày trả hàng (kế hoạch)",
      dataIndex: "returnDate",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "dateRange",
        filterProps: {
          placeholder: "Chọn ngày trả hàng",
        },
      },
      editType: "DatePicker",
      editProps: {
        allowClear: false,
      },
      colReturn: (row: any) => dayjs(row).format("DD/MM/YYYY"),
    },
    {
      title: "Khách hàng",
      dataIndex: "customerName",
      filterDataIndex: "customer",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "debounce",
        filterProps: {
          showSearch: true,
          placeholder: "Chọn khách hàng",
          fetchOptions: apiDebounceCustomer,
        },
      },
      editType: "LookupTableSelect",
      editProps: {
        placeholder: "Chọn khách hàng",
        fetchOptions: apiDebounceCustomer,
        ...customerLookupTable,
      },
      colReturn: (row: any) => row?.label || row,
    },
    {
      title: "Hình thức VC",
      dataIndex: "transportType",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          placeholder: "Chọn hình thức VC",
          options: options.ORDER_TRANSPORT_TYPE,
        },
      },
      editType: "SelectOption",
      editProps: {
        placeholder: "Chọn hình thức VC",
        options: options.ORDER_TRANSPORT_TYPE,
      },
      colReturn: (row: any) =>
        options.ORDER_TRANSPORT_TYPE.find((item: any) => item.value === row)
          ?.label || row,
    },
    {
      title: "Hàng hoá",
      dataIndex: "productName",
      fixed: false,
      hidden: false,
      editType: "Input",
      editProps: {
        placeholder: "Nhập hàng hoá",
      },
    },
    {
      title: "Số cont",
      dataIndex: "containerNumber",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Số cont",
        },
      },
      editType: "Input",
      editProps: {
        placeholder: "Nhập số cont",
      },
    },
    {
      title: "Loại cont",
      dataIndex: "containerType",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          placeholder: "Chọn loại cont",
          options: options.ORDER_CONT_TYPE.map((item: any) => ({
            label: item,
            value: item,
          })),
        },
      },
      editType: "SelectOption",
      editProps: {
        placeholder: "Chọn loại cont",
        options: options.ORDER_CONT_TYPE.map((item: any) => ({
          label: item,
          value: item,
        })),
      },
    },
    {
      title: "Khối lượng (Tấn)",
      dataIndex: "weight",
      fixed: false,
      hidden: false,
      editType: "InputNumber",
      editProps: {
        placeholder: "Nhập khối lượng",
        min: 0,
      },
    },
    {
      title: "Phương thức giao nhận",
      dataIndex: "deliveryMethod",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          placeholder: "Chọn phương thức giao nhận",
          options: options.ORDER_DELIVERY_METHOD,
        },
      },
      editType: "SelectOption",
      editProps: {
        placeholder: "Chọn phương thức giao nhận",
        options: options.ORDER_DELIVERY_METHOD,
      },
      colReturn: (row: any) =>
        options.ORDER_DELIVERY_METHOD.find((item: any) => item.value === row)
          ?.label || row,
    },
    {
      title: "PAKD",
      dataIndex: "businessPlanCode",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập mã PAKD",
        },
      },
      editType: "LookupTableSelect",
      editProps: (cellValue: any, record: any, editIndex: any) => {
        const editItem = editIndex?.find(
          (ind: any) => ind.index === record._id,
        );
        const editRow = editItem?.record;
        return {
          placeholder: "Chọn PAKD",
          showSearch: true,
          allowClear: true,
          ...businessPlanLookupTable,
          fetchOptions: (
            search: string,
            _value?: string,
            filters: LookupFilters = {},
          ) => {
            return apiDebouncePAKD(
              search,
              editRow?.customerName?.value ?? record.customer?._id,
              filters,
            );
          },
          disabled: shouldDisableOrderBusinessPlanSelect({
            record,
            editItem,
          }),
        };
      },
      colReturn: (row: any) => row?.label || row,
    },
    {
      title: "Tuyến",
      dataIndex: "routeCode",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập tuyến",
        },
      },
      editType: "LookupTableSelect",
      editProps: (cellValue: any, record: any, editIndex: any) => {
        const editItem = editIndex?.find(
          (ind: any) => ind.index === record._id,
        );

        return {
          placeholder: "Chọn tuyến",
          showSearch: true,
          allowClear: true,
          ...routeBaseLookupTable,
          fetchOptions: apiDebounceRoute,
          disabled: shouldDisableOrderRouteBaseSelect({
            record,
            editItem,
          }),
        };
      },
      colReturn: (row: any, record: any) =>
        row || record.businessPlan?.routeBaseCode,
    },
    {
      title: "Chiều hàng",
      dataIndex: "direction",
      filterDataIndex: "direction",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          showSearch: true,
          optionFilterProp: "label",
          options: permissionDirections,
          placeholder: "Nhập chiều hàng",
        },
      },
      editType: "Input",
      editProps: {
        placeholder: "Chiều hàng",
        disabled: true,
      },
      colReturn: (row: any, record: any) => row || getOrderDirection(record),
    },
    {
      title: "Điểm nhận",
      dataIndex: "receiptAddress",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập điểm nhận",
        },
      },
      editType: "Input",
      editProps: {
        placeholder: "Điểm nhận",
        disabled: true,
      },
    },
    {
      title: "Điểm trả",
      dataIndex: "deliveryAddress",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập điểm trả",
        },
      },
      editType: "Input",
      editProps: {
        placeholder: "Điểm trả",
        disabled: true,
      },
    },
    {
      title: "Điểm đóng chi tiết",
      dataIndex: "loadingDetail",
      fixed: false,
      hidden: false,
      // filterDataIndex: "receipt",
      // filterConfig: {
      //   type: "string",
      //   filterProps: {
      //     placeholder: "Nhập điểm đóng chi tiết",
      //   },
      // },
      editType: "Input",
      editProps: {
        placeholder: "Nhập điểm đóng chi tiết",
      },
    },
    {
      title: "Điểm trả chi tiết",
      dataIndex: "returnDetail",
      fixed: false,
      hidden: false,
      // filterDataIndex: "delivery",
      // filterConfig: {
      //   type: "string",
      //   filterProps: {
      //     placeholder: "Nhập điểm trả chi tiết",
      //   },
      // },
      editType: "Input",
      editProps: {
        placeholder: "Nhập điểm trả chi tiết",
      },
    },
    {
      title: "Doanh thu",
      dataIndex: "estimatedRevenue",
      fixed: false,
      hidden: false,
      render: (row: any) => (row ? row.toLocaleString("vi-VN") : ""),
    },
    creatorColumn,
    {
      title: "Trạng thái",
      dataIndex: "status",
      fixed: "right",
      hidden: false,
      render: (row: any) => {
        const statusObj = options.ORDER_V2_STATUS.find(
          (item: any) => item.value === row,
        );
        return (
          <div style={{ color: statusObj?.color, fontWeight: "bold" }}>
            {statusObj ? statusObj.label : row}
          </div>
        );
      },
    },
  ];
  const columnsBaseDirectOrderTableConfig = [
    {
      title: "#",
      dataIndex: "_id",
      width: 50,
      fixed: false,
      hidden: false,
      render: (row: any, record: any, index: number) => index + 1,
    },
    {
      title: "Mã đơn hàng",
      dataIndex: "code",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập mã đơn hàng",
        },
      },
    },
    {
      title: "Hình thức sở hữu",
      dataIndex: "ownership",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          placeholder: "Chọn hình thức sở hữu",
          options: options.ORDER_OWNERSHIP,
        },
      },
      render: (row: any) =>
        options.ORDER_OWNERSHIP.find((item: any) => item.value === row)
          ?.label || row,
    },
    {
      title: "Khu vực",
      dataIndex: ["region", "name"],
      filterDataIndex: "region",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          placeholder: "Chọn khu vực",
          options: permissionRegions,
          allowClear: true,
          showSearch: true,
        },
      },
    },
    {
      title: "Ngày đóng hàng",
      dataIndex: "loadingDate",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "dateRange",
        filterProps: {
          placeholder: "Chọn ngày đóng hàng",
        },
      },
      render: (row: any) => dayjs(row).format("DD/MM/YYYY"),
    },
    {
      title: "Ngày trả hàng",
      dataIndex: "returnDate",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "dateRange",
        filterProps: {
          placeholder: "Chọn ngày trả hàng",
        },
      },
      editType: activeTab === "3" ? undefined : "DatePicker",
      editProps: {
        allowClear: false,
      },
      render: (row: any) => dayjs(row).format("DD/MM/YYYY"),
      colReturn: (row: any) => dayjs(row).format("DD/MM/YYYY"),
    },
    {
      title: "Khách hàng",
      dataIndex: "customerName",
      filterDataIndex: "customer",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập khách hàng",
        },
      },
    },
    {
      title: "Hình thức VC",
      dataIndex: "transportType",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          placeholder: "Chọn hình thức VC",
          options: options.ORDER_TRANSPORT_TYPE,
        },
      },
      render: (row: any) =>
        options.ORDER_TRANSPORT_TYPE.find((item: any) => item.value === row)
          ?.label || row,
    },
    {
      title: "Hàng hoá",
      dataIndex: "productName",
      fixed: false,
      hidden: false,
    },
    {
      title: "Số cont",
      dataIndex: "containerNumber",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Số cont",
        },
      },
      editType: activeTab === "3" ? undefined : "Input",
      editProps: {
        placeholder: "Nhập số cont",
      },
    },
    {
      title: "Loại cont",
      dataIndex: "containerType",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          placeholder: "Chọn loại cont",
          options: options.ORDER_CONT_TYPE.map((item: any) => ({
            label: item,
            value: item,
          })),
        },
      },
    },
    {
      title: "Khối lượng (Tấn)",
      dataIndex: "weight",
      fixed: false,
      hidden: false,
    },
    {
      title: "Phương thức giao nhận",
      dataIndex: "deliveryMethod",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          placeholder: "Chọn phương thức giao nhận",
          options: options.ORDER_DELIVERY_METHOD,
        },
      },

      render: (row: any) =>
        options.ORDER_DELIVERY_METHOD.find((item: any) => item.value === row)
          ?.label || row,
    },
    {
      title: "PAKD",
      dataIndex: "businessPlanCode",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập mã PAKD",
        },
      },

      // render: (row: any) => row?.label || row,
    },
    {
      title: "Tuyến",
      dataIndex: "routeCode",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập tuyến",
        },
      },
      render: (row: any, record: any) =>
        row || record.businessPlan?.routeBaseCode,
    },
    {
      title: "Chiều hàng",
      dataIndex: "direction",
      filterDataIndex: "direction",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          showSearch: true,
          optionFilterProp: "label",
          options: permissionDirections,
          placeholder: "Nhập chiều hàng",
        },
      },
      render: (row: any, record: any) => row || getOrderDirection(record),
    },
    {
      title: "Điểm nhận",
      dataIndex: "receiptAddress",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập điểm nhận",
        },
      },
    },
    {
      title: "Điểm trả",
      dataIndex: "deliveryAddress",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập điểm trả",
        },
      },
    },
    {
      title: "Điểm đóng chi tiết",
      dataIndex: "loadingDetail",
      fixed: false,
      hidden: false,
      // filterDataIndex: "receipt",
      // filterConfig: {
      //   type: "string",
      //   filterProps: {
      //     placeholder: "Nhập điểm đóng chi tiết",
      //   },
      // },
    },
    {
      title: "Điểm trả chi tiết",
      dataIndex: "returnDetail",
      fixed: false,
      hidden: false,
      // filterDataIndex: "delivery",
      // filterConfig: {
      //   type: "string",
      //   filterProps: {
      //     placeholder: "Nhập điểm trả chi tiết",
      //   },
      // },
    },
    {
      title: "Nhà xe",
      dataIndex: "truckVendorName",
      filterDataIndex: "truckVendor",
      fixed: false,
      hidden: false,
      width: 150,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập nhà xe",
        },
      },
      editType: activeTab === "3" ? undefined : "LookupTableSelect",
      editProps: (row: any, record: any) => {
        return {
          placeholder: "Chọn nhà xe",
          showSearch: true,
          allowClear: true,
          ...truckVendorLookupTable,
          fetchOptions: (
            search: string,
            _value?: string,
            filters: LookupFilters = {},
          ) => apiDebounceTruckVendor(search, "", "", filters),
          size: "middle",
        };
      },
      colReturn: (row: any) => row?.label || row,
    },
    {
      title: "Biển số xe",
      dataIndex: "licensePlate",
      filterDataIndex: "licensePlate",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập biển số xe",
        },
      },
      colReturn: (row: any) => row?.label || row,
    },
    {
      title: "Doanh thu",
      dataIndex: activeTab === "3" ? "actualRevenue" : "estimatedRevenue",
      fixed: false,
      hidden: false,
      render: (row: any) => (row ? row.toLocaleString("vi-VN") : 0),
    },
    {
      title: "KH doanh thu",
      dataIndex: "combinedRevenueRate",
      fixed: false,
      hidden: false,
      editType: "SelectOption",
      editProps: (row: any, record: any) => {
        return {
          placeholder: "Chọn KH doanh thu",
          allowClear: true,
          options: options.ORDER_COMBINED_RATE_STATUS.map((item: any) => ({
            label: item,
            value: item,
          })),
          disabled: record.costStatus !== "INIT",
        };
      },
    },
    {
      title: "Tổng chi phí thực tế",
      dataIndex: activeTab === "3" ? "actualCost" : "estimatedCost",
      fixed: false,
      hidden: false,
      render: (row: any) => (row ? row.toLocaleString("vi-VN") : 0),
    },
    {
      title: "KH chi phí thầu",
      dataIndex: "combinedCostRate",
      fixed: false,
      hidden: false,
      editType: "SelectOption",
      editProps: (row: any, record: any) => {
        return {
          placeholder: "Chọn KH chi phí thầu",
          allowClear: true,
          options: options.ORDER_COMBINED_RATE_STATUS.map((item: any) => ({
            label: item,
            value: item,
          })),
          disabled: record.costStatus !== "INIT",
        };
      },
    },
    creatorColumn,
    {
      title: "Trạng thái",
      dataIndex: "status",
      fixed: "right",
      hidden: false,
      render: (row: any) => {
        const statusObj = options.ORDER_V2_STATUS.find(
          (item: any) => item.value === row,
        );
        return (
          <div style={{ color: statusObj?.color, fontWeight: "bold" }}>
            {statusObj ? statusObj.label : row}
          </div>
        );
      },
    },
  ];
  const columnnsExtendHistoryOrderTableConfig = [
    {
      title: "Trạng thái chi phí",
      dataIndex: "costStatus",
      fixed: "right",
      hidden: false,
      render: (row: any) => {
        const statusObj = options.ORDER_V2_COST_STATUS.find(
          (item: any) => item.value === row,
        );
        return (
          <div style={{ color: statusObj?.color, fontWeight: "bold" }}>
            {statusObj ? statusObj.label : row}
          </div>
        );
      },
    },
    {
      title: "Kỳ ghi nhận",
      dataIndex: "orderStagePeriod",
      fixed: "right",
      hidden: false,
      render: formatOrderStagePeriod,
    },
  ];
  // const [createOrderTableConfig, setCreateOrderTableConfig] = useState(
  //   columnsBaseCreateOrderTableConfig,
  // );
  const columns = (activeTabs: string): any[] => {
    switch (activeTabs) {
      case "1":
        return columnsBaseCreateOrderTableConfig;
      case "2":
        return columnsBaseDirectOrderTableConfig;
      case "3":
        return [
          ...columnsBaseDirectOrderTableConfig,
          ...columnnsExtendHistoryOrderTableConfig,
        ];
      // case "4":
      //   return [];
      default:
        return columnsBaseCreateOrderTableConfig;
    }
  };

  return columns(activeTab);
};
